import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import {
  configItemsToRecord,
  usePlanogramComplianceConfig,
} from '../../hooks/usePlanogramComplianceConfig';
import { useProductRecognition } from '../../hooks/useProductRecognition';
import { ApiService } from '../../services/api';
import { PlanogramComplianceResponse } from '../../types';
import { Audit } from './components/Audit';

interface ImageAuditPageProps {
  imageId: string | null;
}

export const ImageAuditPage: React.FC<ImageAuditPageProps> = ({ imageId }) => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();

  // Use the product recognition hook
  const {
    productRecognitionData,
    setProductRecognitionData,
    isRunningModel,
    runProductRecognitionModel,
  } = useProductRecognition();

  const { config: planogramComplianceConfig } = usePlanogramComplianceConfig(
    imageId ? Number(imageId) : null
  );

  // State for planogram compliance data
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);

  useEffect(() => {
    if (!imageId || !token) return;
    runProductRecognitionModel(Number(imageId), { useApiCache: true });
  }, [imageId, token, runProductRecognitionModel]);

  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (
        !token ||
        !productRecognitionData ||
        planogramComplianceData ||
        !planogramComplianceConfig
      ) {
        return;
      }
      try {
        const payload = {
          ...productRecognitionData,
          config: configItemsToRecord(planogramComplianceConfig),
        };
        const data = await ApiService.createPlanogramComplianceFromPayload(token, payload, true);
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [
    token,
    productRecognitionData,
    planogramComplianceData,
    planogramComplianceConfig,
    showErrorToast,
  ]);

  return (
    <Audit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      isLoading={isRunningModel}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
      defaultPlanogramComplianceConfig={planogramComplianceConfig}
    />
  );
};

export default ImageAuditPage;
